import React from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';

import FallbackPage from './FallbackPage';
import { ASSET_NAME } from './LandingPage.duck';
import { fetchFeaturedListings } from '../../ducks/featuredListings.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { getFeaturedListingsProps } from '../../util/data';

// Custom section components (PageBuilder section mapping pattern from bookyrslf)
import HeroSection from './CustomSections/HeroSection';
import FeaturedListingsSection from './CustomSections/FeaturedListingsSection';
import SpotlightDealSection from './CustomSections/SpotlightDealSection';
import RecentlyReducedSection from './CustomSections/RecentlyReducedSection';
import CategoriesSection from './CustomSections/CategoriesSection';
import ValuePropsSection from './CustomSections/ValuePropsSection';
import BlogPreviewSection from './CustomSections/BlogPreviewSection';
import AboutSection from './CustomSections/AboutSection';
import HowItWorksSection from './CustomSections/HowItWorksSection';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

/**
 * Map Console section names to custom section types.
 * Console sections with these sectionNames get rendered with our custom components.
 * All other sections pass through to the default SectionBuilder components.
 *
 * To configure: In Sharetribe Console, create landing page sections with these sectionNames.
 * The custom components will render with Replit design while using Console content.
 */
const SECTION_TYPE_MAP = {
  hero: 'constructionHero',
  'featured-listings': 'constructionFeaturedListings',
  'hot-listings': 'constructionFeaturedListings',
  spotlight: 'constructionSpotlight',
  'spotlight-deal': 'constructionSpotlight',
  'recently-reduced': 'constructionRecentlyReduced',
  reduced: 'constructionRecentlyReduced',
  categories: 'constructionCategories',
  'value-props': 'constructionValueProps',
  offers: 'constructionValueProps',
  'special-offers': 'constructionValueProps',
  blog: 'constructionBlog',
  'blog-preview': 'constructionBlog',
  // Content page sections
  about: 'constructionAbout',
  'about-page': 'constructionAbout',
  'how-it-works': 'constructionHowItWorks',
  'how-it-works-page': 'constructionHowItWorks',
};

/**
 * Custom section components registered with PageBuilder.
 * SectionBuilder merges these with defaultSectionComponents,
 * so custom types take priority while standard types still work.
 */
const CUSTOM_SECTION_COMPONENTS = {
  constructionHero: { component: HeroSection },
  constructionFeaturedListings: { component: FeaturedListingsSection },
  constructionSpotlight: { component: SpotlightDealSection },
  constructionRecentlyReduced: { component: RecentlyReducedSection },
  constructionCategories: { component: CategoriesSection },
  constructionValueProps: { component: ValuePropsSection },
  constructionBlog: { component: BlogPreviewSection },
  constructionAbout: { component: AboutSection },
  constructionHowItWorks: { component: HowItWorksSection },
};

/**
 * Default sections to inject when Console hasn't defined matching sectionNames.
 * These render with fallback content from the custom components.
 * Order here determines the section order on the landing page.
 */
const DEFAULT_SECTIONS = [
  { sectionId: 'hero', sectionName: 'hero', sectionType: 'constructionHero' },
  { sectionId: 'featured-listings', sectionName: 'featured-listings', sectionType: 'constructionFeaturedListings' },
  { sectionId: 'spotlight', sectionName: 'spotlight', sectionType: 'constructionSpotlight' },
  { sectionId: 'recently-reduced', sectionName: 'recently-reduced', sectionType: 'constructionRecentlyReduced' },
  { sectionId: 'categories', sectionName: 'categories', sectionType: 'constructionCategories' },
  { sectionId: 'special-offers', sectionName: 'special-offers', sectionType: 'constructionValueProps' },
  { sectionId: 'blog', sectionName: 'blog', sectionType: 'constructionBlog' },
];

/**
 * Build the final sections array:
 * 1. Check if Console has any sections that match our custom section types
 * 2. If yes: use Console sections (mapped to custom types): Console is configured
 * 3. If no: use only our DEFAULT_SECTIONS, ignoring generic Sharetribe defaults
 *
 * This prevents the generic Sharetribe "This is your marketplace" default content
 * from showing alongside our custom ConstructionYardSale sections.
 */
const buildSections = sections => {
  const consoleSections = sections || [];

  // Check if ANY Console section matches our custom section names
  const hasCustomConsoleContent = consoleSections.some(
    s => SECTION_TYPE_MAP[s.sectionName]
  );

  if (!hasCustomConsoleContent) {
    // Console hasn't been configured for ConstructionYardSale yet.
    // Use only our custom default sections (skip generic Sharetribe defaults)
    return DEFAULT_SECTIONS;
  }

  // Console IS configured: map matching sections to custom types,
  // keep non-matching Console sections, inject missing custom defaults
  const mappedSections = consoleSections.map(section => {
    const customType = SECTION_TYPE_MAP[section.sectionName];
    if (customType) {
      return { ...section, sectionType: customType };
    }
    return section;
  });

  const existingCustomTypes = new Set(
    mappedSections
      .map(s => s.sectionType)
      .filter(t => CUSTOM_SECTION_COMPONENTS[t])
  );

  const missingDefaults = DEFAULT_SECTIONS.filter(
    ds => !existingCustomTypes.has(ds.sectionType)
  );

  return [...mappedSections, ...missingDefaults];
};

export const LandingPageComponent = props => {
  const { pageAssetsData, inProgress, error } = props;

  const pageData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;

  // Build sections: map Console sections + inject defaults for missing custom sections
  const customPageData = pageData
    ? {
        ...pageData,
        sections: buildSections(pageData.sections),
      }
    : {
        sections: DEFAULT_SECTIONS,
      };

  return (
    <PageBuilder
      pageAssetsData={customPageData}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage error={error} />}
      featuredListings={getFeaturedListingsProps(camelize(ASSET_NAME), props)}
      options={{
        sectionComponents: CUSTOM_SECTION_COMPONENTS,
      }}
    />
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const featuredListingData = state.featuredListings || {};

  const getListingEntitiesById = listingIds => getListingsById(state, listingIds);

  return { pageAssetsData, featuredListingData, getListingEntitiesById, inProgress, error };
};

const mapDispatchToProps = dispatch => ({
  onFetchFeaturedListings: (sectionId, parentPage, listingImageConfig, allSections) =>
    dispatch(fetchFeaturedListings({ sectionId, parentPage, listingImageConfig, allSections })),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(LandingPageComponent);

export default LandingPage;
